import type { FieldMetaData } from "./fieldsMetaData";
import { createFieldMetaData } from "./createFieldMetaData";
import moment from "moment";
import { getAppointmentPhase } from "../enumutils";

export interface FlightFormState {
  flightId: number
  artist: server.dto.ArtistSmall
  phase: server.dto.AppointmentPhase
  travelAgency: string
  notes: string
  flightType: server.dto.FlightTypes

  airline: server.dto.Airline
  flightDesignator: string

  departureLocal: moment.Moment
  departureAirport: server.dto.Airport
  departureTerminal: string

  arrivalLocal: moment.Moment
  arrivalAirport: server.dto.Airport
  arrivalTerminal: string

  access: string
  confirmationNumber: string
  capacity: number

  passengers: server.dto.FlightPassenger[]
}

export const mapFlightToState = (
    flight: server.dto.Flight,
    passengers: server.dto.FlightPassenger[],
    serverReference: server.dto.ServerReference,
): FlightFormState => ({
  flightId: flight.flightId,
  artist: flight.artist,
  phase: getAppointmentPhase(flight.phase, serverReference),
  travelAgency: flight.travelAgency,
  notes: flight.notes,
  flightType: flight.flightType,
  airline: flight.airline,
  flightDesignator: flight.flightDesignator,

  departureLocal: moment.utc(flight.dateTime.startDisplay),
  departureAirport: flight.departure,
  departureTerminal: flight.departureTerminal,

  arrivalLocal: moment.utc(flight.dateTime.endDisplay),
  arrivalAirport: flight.arrival,
  arrivalTerminal: flight.arrivalTerminal,

  access: flight.access,
  confirmationNumber: flight.confirmationNumber,
  capacity: flight.capacity,

  passengers: passengers,
});

export const getUpdateFlightInstruction = (f: FlightFormState): server.dto.UpdateFlightInstruction => ({
  access: f.access,
  airlineId: f.airline?.airlineId || 0,
  arrivalAirportId: f.arrivalAirport.airportId,
  arrivalLocal: f.arrivalLocal.toDate(),
  arrivalTerminal: f.arrivalTerminal,
  artistId: f.artist.artistId,
  capacity: f.capacity ?? 0,
  confirmationNumber: f.confirmationNumber,
  departureAirportId: f.departureAirport.airportId,
  departureLocal: f.departureLocal.toDate(),
  departureTerminal: f.departureTerminal,
  flightDesignator: f.flightDesignator,
  flightId: f.flightId,
  notes: f.notes,
  phase: f.phase.value,
  travelAgency: f.travelAgency,
});

export interface FlightFieldMetaData {
    artist: FieldMetaData;
    phase: FieldMetaData;
    travelAgency: FieldMetaData;
    notes: FieldMetaData;
    airline: FieldMetaData;
    flightDesignator: FieldMetaData;
    access: FieldMetaData;
    confirmationNumber: FieldMetaData;
    capacity: FieldMetaData;

    departureLocal: FieldMetaData;
    departureAirport: FieldMetaData;
    departureTerminal: FieldMetaData;

    arrivalLocal: FieldMetaData;
    arrivalAirport: FieldMetaData;
    arrivalTerminal: FieldMetaData;

}

export const getFlightFieldMetaData = (
    environment: server.dto.SelectedEnvironment,
    item: FlightFormState,
    itemPermissions: server.dto.ObjectPermissions
): FlightFieldMetaData => ({
    artist: createFieldMetaData(
        !item.artist,
        !item.artist && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    phase: createFieldMetaData(
        false, //Phase is never empty
        false, //Phase is never hidden
        !itemPermissions.canModify,
    ),

    travelAgency: createFieldMetaData(
        !item.travelAgency,
        !item.travelAgency && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    notes: createFieldMetaData(
        !item.notes,
        !item.notes && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    airline: createFieldMetaData(
        !item.airline,
        item.flightType !== "commercial" || (!item.airline && !itemPermissions.canModify), //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    flightDesignator: createFieldMetaData(
        !item.flightDesignator,
        item.flightType !== "commercial" || (!item.flightDesignator && !itemPermissions.canModify), //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    access: createFieldMetaData(
        !item.access,
        item.flightType !== "private" || (!item.access && !itemPermissions.canModify), //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    confirmationNumber: createFieldMetaData(
        !item.confirmationNumber,
        item.flightType !== "private" || (!item.confirmationNumber && !itemPermissions.canModify), //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    capacity: createFieldMetaData(
        !item.capacity,
        item.flightType !== "private" || (!item.capacity && !itemPermissions.canModify), //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    departureLocal: createFieldMetaData(
        false,
        false,
        !itemPermissions.canModify,
    ),
    departureAirport: createFieldMetaData(
        false,
        false,
        !itemPermissions.canModify,
    ),
    departureTerminal: createFieldMetaData(
        !item.departureTerminal,
        !item.departureTerminal && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    arrivalLocal: createFieldMetaData(
        false,
        false,
        !itemPermissions.canModify,
    ),

    arrivalAirport: createFieldMetaData(
        false,
        false,
        !itemPermissions.canModify,
    ),

    arrivalTerminal: createFieldMetaData(
        !item.arrivalTerminal,
        !item.arrivalTerminal && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    

});